import { USE_MOCK } from '@/api/client';
import type { GrowthWindow, MyStatsResponse, PersonalGrowthResponse, PersonalResponse } from '@/api/types';
import {
  getAvgByFundamental,
  getOverallAvg,
  getScoresForUser,
} from '@/data/mockSafeData';

/** Promedio y número de opiniones de un fundamental para una persona. */
export interface FundAvg {
  id: string;
  avg: number;
  count: number;
}

/** Promedio y número de opiniones de un comportamiento para una persona. */
export interface AttitudeAvg {
  id: string;
  avg: number;
  count: number;
}

/** Rango de fechas de una ventana móvil de 30 días. */
export interface ScoreWindow {
  startDate: string;
  endDate: string;
}

export interface SafeScores {
  /** Promedios de la ventana "hoy" (últimos 30 días) por fundamental; alimenta la comparación. */
  fundAvgs: FundAvg[];
  /** Promedios acumulados de toda la vida; alimentan las barras y las tarjetas. */
  accumulatedFundAvgs: FundAvg[];
  /** Total de evaluaciones recibidas; alimenta el umbral del dashboard. */
  totalEvals: number;
  /** Promedio de la ventana "hoy". */
  currentAvg: number;
  /** Promedio de la ventana "hace 3 meses". */
  pastAvg: number;
  pastFundAvgs: FundAvg[];
  /** Promedios de la ventana "hoy" por comportamiento; alimenta la araña cuando el
   *  fundamental tiene comportamientos activos. Vacío si el catálogo/backend no los trae. */
  attitudeAvgs: AttitudeAvg[];
  /** Equivalente de `attitudeAvgs` para la ventana "hace 3 meses". */
  pastAttitudeAvgs: AttitudeAvg[];
  /** Fechas de la ventana "hoy", para la etiqueta de la araña. */
  todayWindow: ScoreWindow;
  /** Fechas de la ventana "hace 3 meses". */
  pastWindow: ScoreWindow;
  progressPct: number;
  answeredCount: number;
  totalCount: number;
}

/** Valores de la barra de participación en modo mock. */
const MOCK_PROGRESS = { pct: 60, answered: 3, total: 5 } as const;
/** Participación asumida cuando la API todavía no reporta estadísticas. */
const DEFAULT_PARTICIPATION_RATE = 0.5;

export interface SafeScoresInput {
  personalData?: PersonalResponse;
  growthData?: PersonalGrowthResponse;
  dashStats?: MyStatsResponse;
  /** Mes en curso en formato YYYY-MM; solo se usa en modo mock. */
  currentMonth: string;
  /** Mes de referencia en formato YYYY-MM. */
  pastMonth: string;
  /** Identificador del usuario de demostración; solo se usa en modo mock. */
  mockUserId: string;
}

/**
 * Normaliza los datos del dashboard personal para que la vista no tenga que
 * distinguir entre el modo mock y las respuestas reales de AnalyticsService.
 */
const EMPTY_WINDOW: ScoreWindow = { startDate: '', endDate: '' };

function toScoreWindow(window: GrowthWindow | undefined): ScoreWindow {
  if (!window) return EMPTY_WINDOW;
  return { startDate: window.startDate, endDate: window.endDate };
}

export function useSafeScores({
  personalData,
  growthData,
  dashStats,
  currentMonth,
  pastMonth,
  mockUserId,
}: SafeScoresInput): SafeScores {
  // Las dos ventanas de 30 días ya las calcula el backend (`today`/`threeMonthsAgo`);
  // el frontend solo las repite para armar la etiqueta de la araña. Se leen igual en
  // modo mock y en modo real: `usePersonalGrowth` devuelve el mismo shape en ambos.
  const todayWindow = toScoreWindow(growthData?.today);
  const pastWindow = toScoreWindow(growthData?.threeMonthsAgo);

  if (USE_MOCK) {
    return {
      fundAvgs: getAvgByFundamental(mockUserId, currentMonth),
      // NOTA: en modo mock no existe un agregado "toda la vida" separado del mensual —
      // getAvgByFundamental no tiene modo "vida completa" — así que accumulatedFundAvgs
      // queda idéntico a fundAvgs. En este modo la araña y las barras siempre van a
      // coincidir; eso no valida el arreglo de ventana móvil, solo lo oculta.
      accumulatedFundAvgs: getAvgByFundamental(mockUserId, currentMonth),
      totalEvals: getScoresForUser(mockUserId, currentMonth).length,
      currentAvg: getOverallAvg(mockUserId, currentMonth),
      pastAvg: getOverallAvg(mockUserId, pastMonth),
      pastFundAvgs: getAvgByFundamental(mockUserId, pastMonth),
      todayWindow,
      pastWindow,
      progressPct: MOCK_PROGRESS.pct,
      answeredCount: MOCK_PROGRESS.answered,
      totalCount: MOCK_PROGRESS.total,
      attitudeAvgs: [],
      pastAttitudeAvgs: [],
    };
  }

  const fundamentalScores = personalData?.fundamentalScores ?? [];
  const answeredCount = dashStats?.totalAnswered ?? 0;

  const toFundAvgs = (window: GrowthWindow | undefined): FundAvg[] =>
    (window?.fundamentals ?? []).map(fundamental => ({
      id: fundamental.fundamentalId,
      avg: fundamental.average,
      count: fundamental.evaluations,
    }));

  const toAttitudeAvgs = (window: GrowthWindow | undefined): AttitudeAvg[] =>
    (window?.attitudes ?? []).map(attitude => ({
      id: attitude.attitudeId,
      avg: attitude.average,
      count: attitude.evaluations,
    }));

  return {
    fundAvgs: toFundAvgs(growthData?.today),
    accumulatedFundAvgs: fundamentalScores.map(fundamental => ({
      id: fundamental.id,
      avg: fundamental.score,
      count: fundamental.evaluations,
    })),
    totalEvals: fundamentalScores.reduce((total, fundamental) => total + fundamental.evaluations, 0),
    currentAvg: growthData?.today?.overallAverage ?? 0,
    pastAvg: growthData?.threeMonthsAgo?.overallAverage ?? 0,
    pastFundAvgs: toFundAvgs(growthData?.threeMonthsAgo),
    todayWindow,
    pastWindow,
    progressPct: Math.round((dashStats?.participationRate ?? DEFAULT_PARTICIPATION_RATE) * 100),
    answeredCount,
    totalCount: answeredCount + (dashStats?.totalOmitted ?? 0),
    attitudeAvgs: toAttitudeAvgs(growthData?.today),
    pastAttitudeAvgs: toAttitudeAvgs(growthData?.threeMonthsAgo),
  };
}
